import {
  getOriginalBoosterNodeId,
  getOriginalChokeNodeId,
  isBoosterNode,
  isChokeNode
} from '../MineModel/Nodes';
import PCPipe from '../MineModel/Pipes/PCPipe';
import PCRoute from '../MineModel/Route/PCRoute';
import PCSpool from '../MineModel/Spools/PCSpool';
import pairKey from '../MineModel/Spools/pairKey';
import {
  DISCHARGE_SPOOL_PREFIX,
  isBorehole,
  isSpoolActive,
  isSyntheticSpool
} from '../MineModel/Spools/utils';
import { Assignment, SegmentInfo, SpoolClass } from './types';

export type SpoolLookup = (nodeAId: string, nodeBId: string) => PCSpool | undefined;

const originalNodeId = (nodeId: string): string => {
  if (isBoosterNode(nodeId)) return getOriginalBoosterNodeId(nodeId);
  if (isChokeNode(nodeId)) return getOriginalChokeNodeId(nodeId);
  return nodeId;
};

export const spoolClassOf = (spool: PCSpool): SpoolClass => (isBorehole(spool.pointA, spool.pointB) ? 'borehole' : 'level');

// The route builder hands a twin the pipe of the first active spool joining the two nodes, so owners resolve the same way.
export const firstActiveSpoolLookup = (spools: Record<string, PCSpool>): SpoolLookup => {
  const byPair = Object.values(spools)
    .filter(isSpoolActive)
    .reduce((acc, spool) => (acc.has(spool.pairKey()) ? acc : acc.set(spool.pairKey(), spool)), new Map<string, PCSpool>());
  return (nodeAId, nodeBId) => byPair.get(pairKey(nodeAId, nodeBId));
};

// A booster or choke swaps the real node -> next spool for node -> twin (zero length) and twin -> next; both halves
// stand for that real spool, which is not on the route itself.
const resolveOwner = (route: PCRoute, index: number, spool: PCSpool, lookup: SpoolLookup): PCSpool | undefined => {
  if (!isSyntheticSpool(spool)) return spool;
  const from = originalNodeId(route.routePointStrings[index]);
  const to = originalNodeId(route.routePointStrings[index + 1]);
  if (from !== to) return lookup(from, to);
  const next = route.routePointStrings[index + 2];
  return next ? lookup(from, originalNodeId(next)) : undefined;
};

export const describeSegments = (route: PCRoute, lookup: SpoolLookup): SegmentInfo[] => route.spools.map((spool, index) => {
  if (spool.spoolId.startsWith(DISCHARGE_SPOOL_PREFIX)) return { kind: 'discharge', isTwinJoin: false };
  const owner = resolveOwner(route, index, spool, lookup);
  return {
    ownerSpoolId: owner?.spoolId,
    kind: spoolClassOf(owner ?? spool),
    isTwinJoin: originalNodeId(route.routePointStrings[index]) === originalNodeId(route.routePointStrings[index + 1]),
  };
});

const withPipe = (spool: PCSpool, pipe: PCPipe): PCSpool => new PCSpool(
  spool.spoolId,
  spool.chokeLength,
  spool.pointA,
  spool.pointB,
  pipe,
  spool.allowFlowAB,
  spool.allowFlowBA,
  {
    spoolColour: spool.spoolColour,
    totalVolumeThroughSpool: spool.totalVolumeThroughSpool,
    totalDryTonnesThroughSpool: spool.totalDryTonnesThroughSpool,
    isInspectionPoint: spool.isInspectionPoint,
    inspectionPointName: spool.inspectionPointName,
    name: spool.name,
    installedOn: spool.installedOn,
    removedOn: spool.removedOn,
    status: spool.status,
    other: spool.other,
    level: spool.level,
    area: spool.area,
  },
);

/**
 * A what-if copy of the route with the assigned pipes. Changed segments get their own spool, so the live network is
 * never repainted; `pipes` must be the model's own instances, because the solver prices friction onto those.
 */
export const buildCandidateRoute = (
  route: PCRoute,
  segments: SegmentInfo[],
  assignment: Assignment,
  pipes: Record<string, PCPipe>,
): PCRoute => new PCRoute(
  route.originNode,
  route.stope,
  route.spools.map((spool, index) => {
    const { ownerSpoolId } = segments[index];
    const assignedPipeId = ownerSpoolId ? assignment.get(ownerSpoolId) : undefined;
    const pipe = assignedPipeId ? pipes[assignedPipeId] : undefined;
    return pipe && pipe !== spool.pipe ? withPipe(spool, pipe) : spool;
  }),
  route.routeLength,
  route.routePointStrings,
);
